/**
 * Probation policies (UX-A7a / EMP-10, Keka's "Probation Policy"): a named probation length in
 * days, weeks or months, and whether probation ends on its own when that time is up. One policy may
 * be the org default — the one Add Employee pre-selects and bulk import applies when a row names
 * none. No policy (and no explicit end date) means no probation: the person starts confirmed.
 *
 * Admin CRUD is `org.settings:read` / `org.settings:update`. The Add Employee picker reads the
 * active ones through `listActiveProbationPolicies` (`employee:create`).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { PROBATION_DURATION_UNITS, PROBATION_MAX_BY_UNIT } from '../lib/employment-start.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const probationPolicySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  durationValue: z.number().int(),
  durationUnit: z.enum(PROBATION_DURATION_UNITS),
  /** Keka's "End employee probation automatically": confirmed the day after the period ends. */
  autoConfirm: z.boolean(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** How many employments currently point at it — a policy in use can't be deleted. */
  employeeCount: z.number().int(),
});
export type ProbationPolicy = z.infer<typeof probationPolicySchema>;

const nameSchema = z.string().trim().min(1).max(100);
const durationValueSchema = z.number().int().min(1).max(730);

export const createProbationPolicyBodySchema = z
  .object({
    name: nameSchema,
    description: z.string().trim().max(500).optional(),
    durationValue: durationValueSchema,
    durationUnit: z.enum(PROBATION_DURATION_UNITS),
    autoConfirm: z.boolean().optional(),
    /** Making this the default un-defaults the previous one. */
    isDefault: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    const max = PROBATION_MAX_BY_UNIT[body.durationUnit];
    if (body.durationValue > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationValue'],
        message: `At most ${max} ${body.durationUnit} (two years)`,
      });
    }
  });

/**
 * Editing the length does not move anyone's existing end date — it applies to new joiners. Turning
 * auto-confirm on or off DOES apply to everyone still on the policy (the daily job reads it); the
 * screen says how many people that is before saving. The per-unit maximum is checked on the merged
 * row by the API.
 */
export const updateProbationPolicyBodySchema = z.object({
  name: nameSchema.optional(),
  description: z.string().trim().max(500).nullable().optional(),
  durationValue: durationValueSchema.optional(),
  durationUnit: z.enum(PROBATION_DURATION_UNITS).optional(),
  autoConfirm: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

const listSchema = z.object({ items: z.array(probationPolicySchema) });

const readMeta = routeMeta('org.settings:read');
const writeMeta = routeMeta('org.settings:update');

export const probationPoliciesContract = c.router(
  {
    listProbationPolicies: {
      method: 'GET',
      path: '/probation-policies',
      responses: { 200: listSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List probation policies, active or not',
      metadata: readMeta,
    },
    createProbationPolicy: {
      method: 'POST',
      path: '/probation-policies',
      body: createProbationPolicyBodySchema,
      responses: {
        201: probationPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Add a probation policy',
      metadata: writeMeta,
    },
    updateProbationPolicy: {
      method: 'PATCH',
      path: '/probation-policies/:policyId',
      body: updateProbationPolicyBodySchema,
      responses: {
        200: probationPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Rename, resize, make default, or deactivate a probation policy',
      metadata: writeMeta,
    },
    deleteProbationPolicy: {
      method: 'DELETE',
      path: '/probation-policies/:policyId',
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a probation policy nobody is on (deactivate one that is in use instead)',
      metadata: writeMeta,
    },
    listActiveProbationPolicies: {
      method: 'GET',
      path: '/probation-policies/active',
      responses: { 200: listSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Active probation policies, default first — for the Add Employee picker',
      metadata: routeMeta('employee:create'),
    },
  },
  { pathPrefix: '/organisation' },
);
